import moment from "moment";
import { convertToInternationalFormat } from "@utils/utils";
import { normalizeHowDidYouKnowFromApi } from "@helpers/customerSourceOptions";
import { resolveAppointmentId } from "@helpers/mergeAppointmentWithMember";
import { resolveAppointmentCounsellorName } from "@utils/brandUserAppointments";

/** Smart Scheduler 事件 id 須唯一；保留 appointment_id 供點擊開啟彈窗 */
export function buildSchedulerEventId(appointment, index = 0) {
  const appointmentId = resolveAppointmentId(appointment);
  const suffix = [
    appointment.start_time ?? "",
    appointment.employee_id ?? "",
    index,
  ].join("-");

  if (appointmentId != null && appointmentId !== "") {
    return `appt-${appointmentId}-${suffix}`;
  }
  return `appt-${appointment.appointment_order_id ?? "o"}-${suffix}`;
}

export function getAppointmentServiceLabel(services) {
  if (!Array.isArray(services) || services.length === 0) return "無項目";
  return services
    .map((s) => s.service_name || s.name)
    .filter(Boolean)
    .join("、");
}

/** 合併多筆預約列的服務（列印／匯出去重時保留全部服務） */
export function mergeAppointmentServices(servicesA = [], servicesB = []) {
  const byKey = new Map();
  [...servicesA, ...servicesB].forEach((s) => {
    if (!s) return;
    const key = s.service_id ?? s.id ?? s.service_name ?? s.name;
    if (key == null) return;
    byKey.set(String(key), s);
  });
  return Array.from(byKey.values());
}

export function getFirstAppointmentServiceName(services) {
  if (!Array.isArray(services) || services.length === 0) return "無項目";
  return services[0]?.service_name || "無項目";
}

/** 預約類別完整名稱（對應日曆色塊上的單字標籤） */
export const APPOINTMENT_TYPE_FULL_LABELS = {
  FOLLOW_UP: "回診",
  CONSULTATION: "諮詢",
  TREATMENT: "耗療",
  SURGERY: "手術",
  AESTHETICS: "美療",
  BLOOD_DRAW: "抽血",
};

/** 預約類別色塊設定（與日曆 eventTemplate 一致） */
export const APPOINTMENT_CATEGORY_TAG_CONFIG = {
  NEW_CUSTOMER: { id: "new-customer", label: "新客", color: "#FBA23A" },
  FOLLOW_UP: { id: "follow-up", label: "回診", color: "#8451FC" },
  CONSULTATION: { id: "consultation", label: "諮詢", color: "#6EA1EE" },
  TREATMENT: { id: "treatment", label: "耗療", color: "#66CA73" },
  SURGERY: { id: "surgery", label: "手術", color: "#D67AFF" },
  AESTHETICS: { id: "aesthetics", label: "美療", color: "#FF85A1" },
  BLOOD_DRAW: { id: "blood-draw", label: "抽血", color: "#E53935" },
};

/** 取得預約類別色塊列表（順序：新客 → 預約類別） */
export function getAppointmentCategoryTags(appointment) {
  if (!appointment) return [];
  const tags = [];
  if (appointment.is_first_visit === true) {
    tags.push(APPOINTMENT_CATEGORY_TAG_CONFIG.NEW_CUSTOMER);
  }
  const types = Array.isArray(appointment.appointment_type)
    ? appointment.appointment_type
    : appointment.appointment_type != null
      ? [appointment.appointment_type]
      : [];
  types.forEach((type) => {
    const config = APPOINTMENT_CATEGORY_TAG_CONFIG[type];
    if (config && !tags.some((t) => t.id === config.id)) {
      tags.push(config);
    }
  });
  return tags;
}

/** @deprecated 請改用 getAppointmentCategoryTags */
export function getAppointmentCategoryFullLabels(appointment) {
  return getAppointmentCategoryTags(appointment).map((t) => t.label);
}

/** 月視圖色塊：僅第一項服務；週、日預設顯示全部（超出由 CSS ellipsis 截斷） */
export function getAppointmentTitleServiceLabel(
  services,
  viewMode,
  serviceFirstOnly = false
) {
  if (serviceFirstOnly || viewMode === "month") {
    return getFirstAppointmentServiceName(services);
  }
  return getAppointmentServiceLabel(services);
}

function isAppointmentMemberLookup(value) {
  return Boolean(value && value.byId instanceof Map && value.byPhone instanceof Map);
}

function pickEarlierMember(current, candidate) {
  if (!candidate) return current;
  if (!current || candidate.index < current.index) return candidate;
  return current;
}

/** 會員名單載入時建一次；色塊依 id、電話直接查，順序與原本從頭找的第一筆相同 */
export function buildAppointmentMemberLookup(members = []) {
  const byId = new Map();
  const byPhone = new Map();
  if (!Array.isArray(members)) return { byId, byPhone };

  members.forEach((member, index) => {
    if (!member || typeof member !== "object") return;
    if (member.id != null && member.id !== "" && !byId.has(member.id)) {
      byId.set(member.id, { member, index });
    }
    if (member.phone != null && member.phone !== "" && !byPhone.has(member.phone)) {
      byPhone.set(member.phone, { member, index });
    }
  });

  return { byId, byPhone };
}

function findAppointmentMemberLinear(eventObj, members) {
  if (!eventObj || !Array.isArray(members)) return null;
  return (
    members.find((member) => {
      if (eventObj.user_id && member.id === eventObj.user_id) return true;
      if (eventObj.user_phone && member.phone === eventObj.user_phone) return true;
      if (eventObj.phone && member.phone === eventObj.phone) return true;
      return false;
    }) || null
  );
}

export function findAppointmentMember(eventObj, membersOrLookup) {
  if (!eventObj) return null;
  if (!isAppointmentMemberLookup(membersOrLookup)) {
    return findAppointmentMemberLinear(eventObj, membersOrLookup);
  }

  let best = null;
  if (eventObj.user_id) {
    best = pickEarlierMember(best, membersOrLookup.byId.get(eventObj.user_id));
  }
  if (eventObj.user_phone) {
    best = pickEarlierMember(best, membersOrLookup.byPhone.get(eventObj.user_phone));
  }
  if (eventObj.phone) {
    best = pickEarlierMember(best, membersOrLookup.byPhone.get(eventObj.phone));
  }
  return best?.member || null;
}

export function resolveAppointmentContactInfo(
  eventObj,
  members = [],
  {
    viewMode = null,
    serviceFirstOnly = false,
    memberLookup = null,
    counsellorResolveOptions = null,
  } = {}
) {
  const relatedMember =
    findAppointmentMember(eventObj, memberLookup || members) || null;

  const rawPhone =
    eventObj.user_phone || eventObj.phone || relatedMember?.phone || "";
  const phone = rawPhone ? convertToInternationalFormat(rawPhone) : "無";

  const rawBirthday =
    eventObj.brand_birthday ||
    relatedMember?.brand_birthday ||
    relatedMember?.birthday;
  const birthday = rawBirthday
    ? moment(rawBirthday).format("YYYY-MM-DD")
    : "無";

  const howDidYouKnowRaw =
    relatedMember?.how_did_you_know ||
    eventObj.how_did_you_know ||
    "尚未確認";
  const howDidYouKnow =
    normalizeHowDidYouKnowFromApi(howDidYouKnowRaw) || howDidYouKnowRaw;

  const resolvedCounsellorName = resolveAppointmentCounsellorName(
    eventObj,
    counsellorResolveOptions || {}
  );

  return {
    relatedMember,
    phone,
    birthday,
    howDidYouKnow,
    serviceLabel: getAppointmentTitleServiceLabel(
      eventObj.services,
      viewMode,
      serviceFirstOnly
    ),
    branchNote: eventObj.branch_note || "",
    displayName:
      eventObj.display_name ||
      eventObj.last_name ||
      eventObj.user_name ||
      "未命名",
    counsellorName: resolvedCounsellorName || "未知姓名",
    categoryTags: getAppointmentCategoryTags(eventObj),
  };
}

function escapeAppointmentHtml(text) {
  return String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

const APPOINTMENT_EVENT_TYPE_TAGS = {
  FOLLOW_UP: { text: "回", color: "#8451FC" },
  CONSULTATION: { text: "諮", color: "#6EA1EE" },
  TREATMENT: { text: "耗", color: "#66CA73" },
  SURGERY: { text: "術", color: "#D67AFF" },
  AESTHETICS: { text: "美", color: "#FF85A1" },
  BLOOD_DRAW: { text: "抽", color: "#E53935" },
};

function appointmentEventDetailLine(text, kind) {
  if (!text) return "";
  const className =
    kind === "time"
      ? "appointment-event-time"
      : kind === "emphasis"
        ? "appointment-event-emphasis"
        : "appointment-event-detail";
  const marginTop = kind === "emphasis" || kind === "time" ? "0" : "-2px";
  return `<p class="${className}" style="margin-top:${marginTop};margin-bottom:0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:100%;line-height:1.3;text-align:left">${escapeAppointmentHtml(text)}</p>`;
}

/** 一次寫入色塊。類別、姓名、時間的 class 與現在畫面相同 */
export function buildAppointmentEventBlockHtml({
  eventObj,
  contactInfo,
  timeLine = "",
  view,
  isMonthSingleAppointment = false,
  serviceFirstOnly = false,
  showField = () => true,
}) {
  const isMonthView = view === "month";
  const isWeekView = view === "week";
  const isDayView = view === "day";
  const isTagsNameSameRow = isDayView || isWeekView || isMonthView;
  const wrapperClass = isMonthView
    ? `appointment-event-wrapper appointment-event-wrapper--month${
        isMonthSingleAppointment ? " appointment-event-wrapper--month-single" : ""
      }`
    : isDayView
      ? "appointment-event-wrapper appointment-event-wrapper--day"
      : isWeekView
        ? "appointment-event-wrapper appointment-event-wrapper--week"
        : "appointment-event-wrapper";
  const typeTagClass = isMonthView
    ? "appointment-event-type-tag appointment-event-type-tag--month d-flex align-items-center"
    : "appointment-event-type-tag px-05 h-18 mr-3 mt-1 d-flex align-items-center";
  const tags = [];
  if (showField("categoryTags") && eventObj?.is_first_visit === true) {
    tags.push(
      `<p class="${typeTagClass}" style="color:#fff;background:#FBA23A;border-radius:2px">新</p>`
    );
  }
  const appointmentTypes = Array.isArray(eventObj?.appointment_type)
    ? eventObj.appointment_type
    : [eventObj?.appointment_type];
  appointmentTypes.forEach((type) => {
    const tag = APPOINTMENT_EVENT_TYPE_TAGS[type];
    if (tag && showField("categoryTags")) {
      tags.push(
        `<p class="${typeTagClass}" style="color:#fff;background-color:${tag.color};border-radius:2px;margin-bottom:0">${tag.text}</p>`
      );
    }
  });

  const nameStyle = isTagsNameSameRow || isMonthView
    ? "overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:100%;text-align:left;flex:1 1 auto;min-width:0;width:auto;display:block"
    : "overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:100%;text-align:left;display:block;width:100%";
  const nameHtml = showField("name")
    ? `<span class="appointment-event-emphasis" style="${nameStyle}">${escapeAppointmentHtml(contactInfo?.displayName || "")}</span>`
    : "";

  const headerClass = isTagsNameSameRow
    ? isMonthView
      ? "appointment-event-header appointment-event-header--month-tags"
      : "appointment-event-header"
    : "";
  const headerStyle = `display:flex;align-items:center;justify-content:start;flex-wrap:${
    isTagsNameSameRow ? "nowrap" : "wrap"
  };max-width:100%;${isTagsNameSameRow ? "width:100%;min-width:0" : ""}`;
  const headerInner = `${tags.join("")}${isTagsNameSameRow && showField("name") ? nameHtml : ""}`;
  const headerHtml = headerInner
    ? `<div class="${headerClass}" style="${headerStyle}">${headerInner}</div>`
    : "";

  const monthTimeHtml =
    isMonthView && showField("time")
      ? `<div class="appointment-event-time-row appointment-event-time-row--month" style="width:100%;flex-shrink:0"><span class="appointment-event-time">${escapeAppointmentHtml(timeLine)}</span></div>`
      : "";
  const nameBelowHtml = !isTagsNameSameRow && showField("name") ? nameHtml : "";

  const lines = [];
  if (!isMonthView && (isWeekView || isDayView) && showField("time")) {
    lines.push(appointmentEventDetailLine(timeLine, "time"));
  }
  if (showField("service")) {
    lines.push(
      appointmentEventDetailLine(
        getAppointmentTitleServiceLabel(eventObj?.services, view, serviceFirstOnly),
        "emphasis"
      )
    );
  }
  if (isMonthSingleAppointment || isWeekView || isDayView) {
    if (showField("phone") && contactInfo?.phone && contactInfo.phone !== "無") {
      lines.push(appointmentEventDetailLine(`電話：${contactInfo.phone}`, "detail"));
    }
    if (showField("birthday") && contactInfo?.birthday && contactInfo.birthday !== "無") {
      lines.push(appointmentEventDetailLine(`生日：${contactInfo.birthday}`, "detail"));
    }
    if (
      showField("counsellor") &&
      contactInfo?.counsellorName &&
      contactInfo.counsellorName !== "未知姓名"
    ) {
      lines.push(
        appointmentEventDetailLine(
          `諮詢師：${contactInfo.counsellorName}`,
          "detail"
        )
      );
    }
    if (showField("note")) {
      lines.push(
        appointmentEventDetailLine(
          contactInfo?.branchNote ? `備註：${contactInfo.branchNote}` : "備註：無",
          "detail"
        )
      );
    }
  }

  return `<div class="${wrapperClass}" style="display:flex;flex-direction:column;align-items:flex-start;width:100%;max-height:100%;overflow:hidden">${headerHtml}${monthTimeHtml}${nameBelowHtml}${lines.join("")}</div>`;
}

export function buildAppointmentCategoryTagsHtml(tags = []) {
  if (!tags.length) return "";
  const escapeHtml = (text) =>
    String(text)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");

  const chips = tags
    .map(
      (tag) =>
        `<span class="appointment-category-tag" style="background-color:${tag.color}">${escapeHtml(tag.label)}</span>`
    )
    .join("");

  return `<span class="appointment-tooltip-category-tags">${chips}</span>`;
}

export function buildAppointmentTooltipHtml(
  info,
  appointmentTime = "",
  visibleFieldKeys = null
) {
  const show = (key) =>
    !visibleFieldKeys || visibleFieldKeys.includes(key);

  const escapeHtml = (text) =>
    String(text)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");

  const emph = (content) =>
    `<span class="appointment-tooltip-emphasis">${content}</span>`;

  const timeLine = (content) =>
    `<span class="appointment-tooltip-time">${content}</span>`;

  const categoryRow = show("categoryTags")
    ? buildAppointmentCategoryTagsHtml(info.categoryTags)
    : "";
  const lines = [];

  if (show("name")) {
    lines.push(emph(escapeHtml(info.displayName)));
  }
  if (show("time")) {
    lines.push(timeLine(escapeHtml(appointmentTime || "無")));
  }
  if (show("service")) {
    lines.push(emph(escapeHtml(info.serviceLabel)));
  }
  if (show("phone")) {
    lines.push(escapeHtml(`電話：${info.phone}`));
  }
  if (show("birthday")) {
    lines.push(escapeHtml(`生日：${info.birthday}`));
  }
  lines.push(escapeHtml(`客戶來源：${info.howDidYouKnow}`));
  lines.push(escapeHtml(`諮詢師：${info.counsellorName}`));
  if (show("note")) {
    lines.push(escapeHtml(`備註：${info.branchNote || "無"}`));
  }

  if (categoryRow) {
    return `${categoryRow}${lines.join("<br>")}`;
  }
  return lines.join("<br>");
}

export function suppressNativeEventTooltip(eventContent) {
  if (!eventContent) return;
  eventContent.removeAttribute("title");
  const eventEl = eventContent.closest(".smart-scheduler-event");
  if (eventEl) eventEl.removeAttribute("title");
}

/** 清除單一預約色塊綁定的 hover tooltip 與 document 追蹤 */
export function cleanupAppointmentEventTooltip(eventContent) {
  if (!eventContent) return;
  if (eventContent.__appointmentMoveHandler) {
    document.removeEventListener("mousemove", eventContent.__appointmentMoveHandler);
    eventContent.__appointmentMoveHandler = null;
  }
  if (eventContent.__appointmentTooltip) {
    eventContent.__appointmentTooltip.remove();
    eventContent.__appointmentTooltip = null;
  }
}

/** 清除頁面上所有預約 hover tooltip（切換色塊、重繪、捲動時避免殘留） */
export function removeAllAppointmentHoverTooltips() {
  document.querySelectorAll(".custom-tooltip").forEach((el) => el.remove());
  document
    .querySelectorAll("[data-appointment-tooltip-bound]")
    .forEach((el) => cleanupAppointmentEventTooltip(el));
}

const APPOINTMENT_HOVER_TOOLTIP_MAX_WIDTH = 280;
const APPOINTMENT_HOVER_TOOLTIP_GAP = 10;

/** 依內容量測後鎖定寬度；高度維持 auto，避免客戶來源／諮詢師等列被鎖死裁切 */
function lockAppointmentHoverTooltipWidth(tooltip) {
  if (!tooltip || tooltip.dataset.sizeLocked === "1") return;

  tooltip.style.width = "auto";
  tooltip.style.height = "auto";
  tooltip.style.minHeight = "0";
  tooltip.style.maxWidth = `${APPOINTMENT_HOVER_TOOLTIP_MAX_WIDTH}px`;
  tooltip.style.maxHeight = "none";
  tooltip.style.overflow = "visible";

  const w = Math.min(
    Math.ceil(tooltip.scrollWidth),
    APPOINTMENT_HOVER_TOOLTIP_MAX_WIDTH
  );

  tooltip.style.width = `${w}px`;
  tooltip.style.minWidth = `${w}px`;
  tooltip.style.maxWidth = `${w}px`;
  tooltip.style.height = "auto";
  tooltip.dataset.sizeLocked = "1";
  tooltip.dataset.lockWidth = String(w);
}

/**
 * 開啟時鎖定寬度、高度隨內容；靠螢幕邊緣時改往反方向偏移，不縮放、不內捲。
 */
export function positionAppointmentHoverTooltip(tooltip, clientX, clientY) {
  if (!tooltip) return;
  lockAppointmentHoverTooltipWidth(tooltip);

  const gap = APPOINTMENT_HOVER_TOOLTIP_GAP;
  const w =
    Number(tooltip.dataset.lockWidth) || tooltip.offsetWidth || 0;
  const h = tooltip.offsetHeight || tooltip.scrollHeight || 0;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  let left = clientX + gap;
  if (left + w > vw - gap) {
    left = clientX - w - gap;
  }
  left = Math.max(gap, Math.min(left, vw - w - gap));

  let top = clientY + gap;
  if (top + h > vh - gap) {
    top = clientY - h - gap;
  }
  top = Math.max(gap, Math.min(top, vh - h - gap));

  tooltip.style.left = `${left}px`;
  tooltip.style.top = `${top}px`;
}
